import React from 'react';
import { useIntl } from 'react-intl';
import { GOOGLE_PRIVACY_URL, YOUTUBE_TERMS_URL } from 'fym-shared/src/music24/youtubePostSettings';
import { TypedMemo } from '../../../../common/types';
import { SerializedSocialChannel } from '../../types';
import { SettingsAccountRow } from '../settings/SettingsAccountRow';
import { SettingsCard } from '../settings/SettingsCard';
import { youtubeMessages } from './youtubeMessages';
import { YouTubeSettingsFields } from './YouTubeSettingsFields';
import type { YouTubeSettingsState } from './useYouTubeSettings';
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the settings sections; each part uses its own slice of it */
import styles from '../settings/PlatformSettings.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerYouTubeSectionProps {
  state: YouTubeSettingsState;
  channel: SerializedSocialChannel;
  /** The post buttons, repeated under the consent line. */
  footer: React.ReactNode;
}

function termsLink(parts: React.ReactNode[]): React.ReactNode {
  return (
    <a href={YOUTUBE_TERMS_URL} target="_blank" rel="noreferrer">
      {parts}
    </a>
  );
}

function privacyLink(parts: React.ReactNode[]): React.ReactNode {
  return (
    <a href={GOOGLE_PRIVACY_URL} target="_blank" rel="noreferrer">
      {parts}
    </a>
  );
}

/**
 * The YouTube settings of a post. It shows when YouTube is selected and its channel posts through
 * the API. The channel (name, avatar) is shown, the privacy status and "Made for kids" start empty
 * and the post stays blocked until the user answers both.
 */
const ComposerYouTubeSectionComponent: React.FC<ComposerYouTubeSectionProps> = ({ state, channel, footer }) => {
  const intl = useIntl();
  const { draft, issues } = state;

  const hint = issues.includes('privacyMissing')
    ? youtubeMessages.needsPrivacy
    : issues.includes('madeForKidsMissing')
      ? youtubeMessages.needsKids
      : null;

  return (
    <SettingsCard
      title={intl.formatMessage(youtubeMessages.title)}
      tag={intl.formatMessage(youtubeMessages.tag)}
      testId="youtube-settings"
      footer={footer}
    >
      <SettingsAccountRow
        eyebrow={intl.formatMessage(youtubeMessages.postingTo)}
        name={channel.connection?.displayName ?? channel.handle ?? 'YouTube'}
        handle={channel.handle}
        avatarUrl={channel.connection?.avatarUrl ?? null}
      />
      <YouTubeSettingsFields draft={draft} onPrivacy={state.setPrivacy} onMadeForKids={state.setMadeForKids} />
      <p className={styles.small}>{intl.formatMessage(youtubeMessages.titleNote)}</p>
      <p className={styles.consent} data-testid="youtube-consent">
        {withBrandWord(intl.formatMessage(youtubeMessages.consent, { terms: termsLink, privacy: privacyLink }))}
      </p>
      {hint === null ? null : (
        <p className={styles.small} data-testid="youtube-hint">
          {intl.formatMessage(hint)}
        </p>
      )}
    </SettingsCard>
  );
};

export const ComposerYouTubeSection = TypedMemo(ComposerYouTubeSectionComponent);
